/**
 * Models and interfaces for Date Range Filter Component
 * 
 * @remarks
 * These models define the structure for date range selection,
 * including preset-based and custom date range selection.
 */

/**
 * Date preset definition for quick date range selection
 * 
 * @remarks
 * Each preset provides a label for display and a function to calculate
 * the actual date range based on current date.
 */
export interface DatePreset {
  /** Display label for the preset button */
  label: string;
  
  /** Unique identifier for the preset */
  value: string;
  
  /** 
   * Function to calculate the date range for this preset
   * Returns dates in ISO 8601 format (yyyy-MM-dd)
   */
  getDateRange: () => DateRangeResult;
}

/**
 * Result of a date range calculation
 * 
 * @remarks
 * Contains the start and end dates in ISO 8601 format.
 * Both dates are in UTC timezone.
 */
export interface DateRangeResult {
  /** Start date in ISO 8601 format (yyyy-MM-dd) */
  dateFrom: string;
  
  /** End date in ISO 8601 format (yyyy-MM-dd) */
  dateTo: string;
}

/**
 * Complete date range value emitted by the component
 * 
 * @remarks
 * This interface represents the full state of the date range filter,
 * including preset selection, custom dates, and validation status.
 */
export interface DateRangeValue {
  /** Selected preset identifier, or null if custom date range */
  preset: string | null;
  
  /** Start date in ISO 8601 format, or null if not set */
  dateFrom: string | null;
  
  /** End date in ISO 8601 format, or null if not set */
  dateTo: string | null;
  
  /** Whether the current date range is valid (dateFrom <= dateTo) */
  isValid: boolean;
}

/**
 * Configuration options for Date Range Filter Component
 * 
 * @remarks
 * These options allow customization of the component's behavior and appearance.
 */
export interface DateRangeFilterConfig {
  /** Label displayed above the filter */
  label?: string;
  
  /** Available date presets */
  presets?: DatePreset[];
  
  /** Whether to show the clear button */
  showClearButton?: boolean;
  
  /** Whether the filter is disabled */
  disabled?: boolean;
  
  /** Whether to show validation errors */
  showValidation?: boolean;
  
  /** Custom validation error message */
  validationErrorMessage?: string;
}

/**
 * Default configuration values
 */
export const DEFAULT_DATE_RANGE_CONFIG: Required<DateRangeFilterConfig> = {
  label: 'Date Range',
  presets: [],
  showClearButton: true,
  disabled: false,
  showValidation: true,
  validationErrorMessage: '"From" date must be before or equal to "To" date'
};
